import React, { useState, useEffect } from "react";
import { useApp } from "../../contexts/AppContext";

const ROLES = ["admin","user","viewer"];

export const UserManagement = () => {
  const { service, addNotification, currentUser } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addSearch, setAddSearch] = useState("");
  const [addRole, setAddRole] = useState("user");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => {
    service.getListItems("AllowedUsers").then(d=>{
      setUsers(d?.value?.map(i=>({...i.fields,id:i.id}))||[]);
    }).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[]);

  useEffect(()=>{
    if(addSearch.length<2){ setSuggestions([]); return; }
    service.searchUsers(addSearch).then(d=>setSuggestions(d?.value||[])).catch(()=>{});
  },[addSearch]);

  const handleAdd = async () => {
    if(!selected) return;
    try {
      await service.createListItem("AllowedUsers",{Title:selected.mail,DisplayName:selected.displayName,Role:addRole,IsActive:true,JobTitle:selected.jobTitle||"",AddedBy:currentUser?.mail,AddedDate:new Date().toISOString()});
      addNotification("User added: "+selected.displayName,"success");
      setSelected(null); setAddSearch(""); setSuggestions([]); load();
    } catch(e){ addNotification("Error: "+e.message,"error"); }
  };

  const toggleActive = async (user) => {
    try {
      await service.updateListItem("AllowedUsers",user.id,{IsActive:!user.IsActive});
      setUsers(prev=>prev.map(u=>u.id===user.id?{...u,IsActive:!u.IsActive}:u));
    } catch(e){ addNotification("Error","error"); }
  };

  const changeRole = async (user, role) => {
    try {
      await service.updateListItem("AllowedUsers",user.id,{Role:role});
      setUsers(prev=>prev.map(u=>u.id===user.id?{...u,Role:role}:u));
    } catch(e){}
  };

  const filtered = users.filter(u=>!search||(u.DisplayName||u.Title||"").toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-slate-100 mb-1">User Management</h1>
        <p className="text-slate-400 m-0">Control which Microsoft 365 users can access LexCore.</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2" />
      </div>

      <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 mb-6">
        <div className="font-serif text-lg font-medium text-yellow-600 mb-4">Add User</div>
        <div className="flex gap-2.5 relative">
          <div className="flex-1 relative">
            <input
              value={addSearch}
              onChange={e=>setAddSearch(e.target.value)}
              placeholder="Search M365 users..."
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
            />
            {selected && <div className="mt-1.5 text-base text-yellow-600">{selected.displayName} - {selected.mail}</div>}
            {suggestions.length>0 && !selected && (
              <div className="absolute top-full left-0 right-0 bg-slate-900 border border-slate-700 rounded-lg z-50 max-h-45 overflow-y-auto shadow-2xl">
                {suggestions.map(u=>(
                  <div
                    key={u.id}
                    onClick={()=>{setSelected(u);setAddSearch(u.displayName);setSuggestions([]);}}
                    className="px-3.5 py-2 cursor-pointer border-b border-slate-700 text-base hover:bg-slate-800"
                  >
                    <div className="font-semibold text-slate-100">{u.displayName}</div>
                    <div className="text-slate-400">{u.mail}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <select
            value={addRole}
            onChange={e=>setAddRole(e.target.value)}
            className="px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 min-w-28"
          >
            {ROLES.map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
          <button
            onClick={handleAdd}
            disabled={!selected}
            className={`px-5 py-2.5 border-none rounded-lg cursor-pointer font-sans font-semibold text-base ${
              selected
                ? "bg-yellow-600 text-slate-900 hover:bg-yellow-500"
                : "bg-slate-700 text-slate-400 cursor-not-allowed"
            }`}
          >
            Add
          </button>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-slate-700 flex justify-between items-center">
          <div className="text-base font-serif font-medium text-slate-100">{users.length} Authorized Users</div>
          <input
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="Filter..."
            className="w-44 px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
          />
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-400">Loading...</div>
        ) : (
          <table className="w-full border-collapse text-base">
            <thead><tr className="bg-slate-950">
              {["User","Email","Role","Status","Action"].map(h=>(<th key={h} className="px-4 py-2.5 text-left text-base font-medium text-slate-400 uppercase tracking-wider border-b border-slate-700">{h}</th>))}
            </tr></thead>
            <tbody>
              {filtered.length===0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-slate-400">No users yet.</td></tr>
              ) : (
                filtered.map((u,i)=>(
                  <tr key={i} className="border-b border-slate-700">
                    <td className="px-4 py-2.5 font-semibold text-slate-100">{u.DisplayName}</td>
                    <td className="px-4 py-2.5 text-base text-slate-400 font-mono">{u.Title}</td>
                    <td className="px-4 py-2.5">
                      <select
                        value={u.Role||"user"}
                        onChange={e=>changeRole(u,e.target.value)}
                        className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-yellow-600 text-base font-sans outline-none focus:border-yellow-600"
                      >
                        {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-base font-semibold ${
                        u.IsActive ? "bg-green-700/20 text-green-700" : "bg-red-700/20 text-red-700"
                      }`}>{u.IsActive?"Active":"Inactive"}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={()=>toggleActive(u)}
                        className="px-2.5 py-1 bg-transparent border border-slate-700 rounded text-slate-400 cursor-pointer text-base font-sans hover:bg-slate-800"
                      >
                        {u.IsActive?"Deactivate":"Activate"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
